/**
 * Page Access Control Module
 * Manages role-based access control for application pages and actions
 * Provides encryption/decryption of access permissions and UI components for access denied scenarios
 * 
 * Features:
 * - Secure storage of page access permissions using AES encryption
 * - Modal component for displaying access denied messages
 * - Utility functions for validating user permissions
 * - Menu filtering based on user access rights
 */

import React from 'react';
import CryptoJS from "crypto-js"; // npm install crypto-js
import { API_SUCCESS_RESPONSE_CODE } from "./config_mapping";
import {pageAccess} from "../services/admin"
import CommonIcon from '../components/common/customicon';

/**
 * AccessDeniedModal Component
 * Displays a modal dialog when user attempts an unauthorized action
 * Features backdrop click to close and navigation buttons
 * 
 * @component
 * @param {Object} props - Component props
 * @param {boolean} props.isOpen - Controls visibility of the modal
 * @param {Function} props.onClose - Callback function when modal is closed
 * @param {string} [props.title="Access Denied"] - Modal title text
 * @param {string} [props.message="You don't have permission to perform this action."] - Main error message
 * @param {string} [props.action="this action"] - Action attempted by user
 * @param {string} [props.page=""] - Page name where access was denied
 * 
 * @example
 * const [accessDenied, setAccessDenied] = useState(null);
 * <AccessDeniedModal 
 *   isOpen={accessDenied?.isOpen} 
 *   onClose={() => setAccessDenied(null)} 
 *   {...accessDenied} 
 * />
 */
export const AccessDeniedModal = ({ 
  isOpen, 
  onClose, 
  title = "Access Denied", 
  message = "You don't have permission to perform this action.",
  action = "this action",
  page = ""
}) => {
  if (!isOpen) return null;

  const handleClose = () => {
    onClose();
  };

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      handleClose();
    }
  };

  return (
    <div 
      className="modal fade show d-block" 
      tabIndex="-1" 
      style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}
      onClick={handleBackdropClick}
    >
      <div className="modal-dialog modal-dialog-centered">
        <div className="modal-content border-0 shadow-lg" style={{ borderRadius: '15px' }}>
          <div className="modal-body text-center p-4">
            {/* Icon */}
            <div
              className="d-flex justify-content-center align-items-center mb-3"
              style={{
                width: "70px",
                height: "70px",
                borderRadius: "50%",
                backgroundColor: "#f8d7da",
                margin: "0 auto",
              }}
            >
              <CommonIcon iconName="Ban" size={"32px"} color="#dc3545" />
            </div>

            {/* Title */}
            <h4 className="fw-bold text-danger mb-3">{title}</h4>

            {/* Message */}
            <p className="text-muted mb-4" style={{ fontSize: '16px' }}>
              {message}
              {page && <><br /><small className="text-secondary">Page: {page}</small></>}
              {action && action !== "this action" && <><br /><small className="text-secondary">Action: {action}</small></>}
            </p>

            {/* Buttons */}
            <div className="d-flex justify-content-center gap-2">
              <button
                type="button"
                className="btn btn-outline-primary rounded-pill px-4"
                onClick={handleClose}
              >
                Close
              </button>
              <button
                type="button"
                className="btn btn-primary rounded-pill px-4"
                onClick={() => window.location.href = "/"}
              >
                Go to Home
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};



/** 
 * LocalStorage key for storing encrypted page access data
 * Access permissions are encrypted before storage for security
 */
const ACCESS_KEY = "page_access_encrypted";

/** 
 * Secret key for AES encryption/decryption of access permissions
 * IMPORTANT: Change this to a strong secret key in production environment
 * Store sensitive values in environment variables instead
 */
const SECRET_KEY = "ContractSecret@123@#@#"; // 🔒 Change this to your own secret key

/**
 * Create access denied modal state object for display
 * Generates modal configuration with title, message, and context information
 * Used to display modal dialog when user lacks required permissions
 * 
 * @param {string} page - Page name where access was denied (e.g., 'Contracts', 'Dashboard')
 * @param {string} action - Action user attempted (e.g., 'view', 'edit', 'delete', 'create')
 * @param {string|null} [customMessage=null] - Custom error message to display
 *                                              If null, generates default message: "You don't have permission to {action} on {page}."
 * @returns {Object} Modal state object
 *                  Properties:
 *                  - isOpen {boolean} - Always true, indicates modal should be displayed
 *                  - title {string} - Modal title: "Access Denied"
 *                  - message {string} - Error message for user
 *                  - page {string} - Page name context
 *                  - action {string} - Action context
 * 
 * @example
 * const modalState = showAccessDeniedModal('Contracts', 'edit');
 * // Returns: {
 * //   isOpen: true,
 * //   title: "Access Denied",
 * //   message: "You don't have permission to edit on Contracts.",
 * //   page: "Contracts",
 * //   action: "edit"
 * // }
 * 
 * @example
 * // With custom message
 * const modalState = showAccessDeniedModal('Admin', 'access', 'Admin access requires additional verification.');
 * // Returns: {
 * //   isOpen: true,
 * //   title: "Access Denied",
 * //   message: "Admin access requires additional verification.",
 * //   page: "Admin",
 * //   action: "access"
 * // }
 */
export function showAccessDeniedModal(page, action, customMessage = null) {
  const defaultMessage = customMessage || `You don't have permission to ${action} on ${page}.`;
  
  return {
    isOpen: true,
    title: "Access Denied",
    message: defaultMessage,
    page: page,
    action: action
  };
}

/**
 * Validate user access and generate access denied modal if permission denied
 * Combines permission checking and modal generation for streamlined access validation
 * Returns modal state if user lacks access, null if access is granted
 * 
 * Process:
 * 1. Checks if user has permission via hasPageAccess()
 * 2. If no permission, creates modal state with showAccessDeniedModal()
 * 3. If permission granted, returns null (no modal needed)
 * 
 * @param {string} pageName - Name of the page to validate access for
 * @param {string} [action='view'] - Action to validate permission for (default: 'view')
 * @param {string|null} [customMessage=null] - Custom message for modal if access denied
 * @returns {Object|null} Modal state object if access denied, null if access granted
 * 
 * @example
 * // Check access on page load
 * const modalState = validatePageAccess('Contracts', 'edit');
 * if (modalState) {
 *   // Display access denied modal
 *   setAccessDeniedModal(modalState);
 * } else {
 *   // User has access, proceed
 *   loadContractData();
 * }
 * 
 * @example
 * // With custom message
 * const modalState = validatePageAccess(
 *   'Admin',
 *   'access',
 *   'This feature requires premium subscription.'
 * );
 * if (modalState) {
 *   setAccessDeniedModal(modalState);
 * }
 */
export function validatePageAccess(pageName, action = "view", customMessage = null) {
  if (!hasPageAccess(pageName, action)) {
    return showAccessDeniedModal(pageName, action, customMessage);
  }
  return null;
}


/**
 * Access denied page component
 * Displays a full-page access denied message when user tries to access restricted pages
 * Provides navigation back to home page
 * 
 * @component
 * @returns {JSX.Element} Access denied page component
 */
export function accessDenied() {
  return (
        <div
      className="d-flex justify-content-center align-items-center vh-80"
      style={{
        // background: "linear-gradient(135deg, #f9fafb, #eef1f6)",
      }}
    >
      <div
        className="card shadow-lg border-0 text-center p-5"
        style={{
          maxWidth: "420px",
          borderRadius: "20px",
          backgroundColor: "#fff",
        }}
      >
        <div
          className="d-flex justify-content-center align-items-center mb-3"
          style={{
            width: "80px",
            height: "80px",
            borderRadius: "50%",
            backgroundColor: "#f8d7da",
            margin: "0 auto",
          }}
        >
          <CommonIcon iconName="Ban" size={"36px"} color="#dc3545" />
        </div>
        <h2 className="fw-bold text-danger mb-2">Access Denied</h2>
        <p className="text-muted mb-4">
          You do not have permission to view this page.
        </p>
        <button
          className="btn btn-outline-primary rounded-pill px-4"
          onClick={() => (window.location.href = "/")}
        >
          Go to Home
        </button>
      </div>
    </div>

  );
}

/**
 * Fetch page access permissions from backend and store encrypted in localStorage
 * Called once on user login to retrieve and cache access rights
 * Encrypts the access data using AES encryption before storage
 * 
 * Process:
 * 1. Calls pageAccess() API to retrieve user permissions
 * 2. Validates successful API response
 * 3. Encrypts permission data using SECRET_KEY
 * 4. Stores encrypted data in localStorage
 * 
 * @async
 * @returns {Promise<void>}
 * 
 * @example
 * useEffect(() => {
 *   fetchAndStorePageAccess(); // Call once on login
 * }, []);
 */
export async function fetchAndStorePageAccess() {
  try {
    //const response = await axios.get("/api/pageaccess"); // Replace with actual API URL

     const response = await pageAccess();
      if (response.code === API_SUCCESS_RESPONSE_CODE) {

        if (response.success === true) {
          const encrypted = CryptoJS.AES.encrypt(
        JSON.stringify(response.data.pageaccess),
        SECRET_KEY
      ).toString();

      localStorage.setItem(ACCESS_KEY, encrypted);
      console.log("Page access data stored securely");
    } else {
      console.error("Access API returned invalid response");
    }
        }
  } catch (err) {
    console.error("Failed to fetch page access:", err);
  }
}

/**
 * Retrieve and decrypt page access permissions from localStorage
 * Decrypts the AES-encrypted access data stored during login
 * 
 * Process:
 * 1. Retrieves encrypted data from localStorage using ACCESS_KEY
 * 2. Decrypts using AES decryption and SECRET_KEY
 * 3. Parses JSON to return access object
 * 4. Returns null if no data found or decryption fails
 * 
 * @returns {Object|null} Decrypted page access array containing user permissions
 *                        Format: [{ name: 'pageName', access: { view: true, edit: false, ... } }, ...]
 *                        Returns null if no access data available or decryption fails
 * 
 * @example
 * const access = getPageAccess();
 * // Returns: [{ name: 'Dashboard', access: { view: true, edit: false } }, ...]
 */
export function getPageAccess() {
  const encrypted = localStorage.getItem(ACCESS_KEY);
  if (!encrypted) return null;

  try {
    const decrypted = CryptoJS.AES.decrypt(encrypted, SECRET_KEY).toString(
      CryptoJS.enc.Utf8
    );
    return JSON.parse(decrypted);
  } catch (err) {
    console.error("Decryption error:", err);
    return null;
  }
}

/**
 * Check if user has permission to access a specific page and perform an action
 * Retrieves decrypted permissions and validates access for the given page and action
 * 
 * Process:
 * 1. Fetches decrypted access data via getPageAccess()
 * 2. Searches for matching page name (case-insensitive)
 * 3. Checks if requested action is permitted
 * 4. Returns false if page or action not found
 * 
 * @param {string} pageName - Name of the page to check access for (e.g., 'Dashboard', 'Contracts')
 * @param {string} [action='view'] - Action to validate (e.g., 'view', 'edit', 'delete', 'create')
 * @returns {boolean} True if user has permission, false otherwise
 * 
 * @example
 * // Check if user can view dashboard
 * if (hasPageAccess('Dashboard')) {
 *   // Show dashboard
 * } else {
 *   // Show access denied
 * }
 * 
 * @example
 * // Check if user can edit contracts
 * if (hasPageAccess('Contracts', 'edit')) {
 *   // Show edit button
 * }
 */
export function hasPageAccess(pageName, action = "view") {
  const accessData = getPageAccess();
  if (!accessData) return false;
  const page = accessData.find(
    (p) => p.name.toLowerCase() === pageName.toLowerCase()
  );
  if (!page) return false;
  return page.access[action] === true;
}

/**
 * Filter menu items based on user access permissions
 * Returns menu array with items disabled if user lacks view permission
 * Used for dynamically filtering navigation menu items based on access control
 * 
 * Process:
 * 1. Retrieves decrypted access data
 * 2. Iterates through provided menu items
 * 3. Finds matching page in access data
 * 4. Sets disabled flag based on 'view' permission
 * 5. Returns modified menu array
 * 
 * @param {Array<Object>} allMenus - Array of menu items to filter
 *                                  Format: [{ name: 'PageName', path: '/path', ... }, ...]
 * @returns {Array<Object>} Menu items with disabled property added
 *                         Format: [{ name: 'PageName', path: '/path', disabled: false, ... }, ...]
 * 
 * @example
 * // Example menu array
 * const allMenus = [
 *   { name: "Dashboard", path: "/dashboard" },
 *   { name: "Contracts", path: "/contracts" },
 *   { name: "Settings", path: "/settings" }
 * ];
 * 
 * const filteredMenus = getAccessibleMenuItems(allMenus);
 * // Returns: [
 * //   { name: "Dashboard", path: "/dashboard", disabled: false },
 * //   { name: "Contracts", path: "/contracts", disabled: true },  // No view access
 * //   { name: "Settings", path: "/settings", disabled: false }
 * // ]
 */
export function getAccessibleMenuItems(allMenus) {
  const accessData = getPageAccess();
  if (!accessData) return [];

  return allMenus.map((menu) => {
    const match = accessData.find(
      (p) => p.name.toLowerCase() === menu.name.toLowerCase()
    );
    return {
      ...menu,
      disabled: !match?.access?.view,
    };
  });
}

// ---------- 5️⃣ Example usage in React ----------
/*
// Example allMenus array
const allMenus = [
  { name: "Documents", path: "/document-list" },
  { name: "Data Explorer", path: "/data-explorer" },
  { name: "Ask Genie", path: "/askgenie" },
  { name: "Taxonomy Management", path: "/taxonomy-management" },
];

useEffect(() => {
  fetchAndStorePageAccess(); // Only once on login
}, []);

const menuItems = getAccessibleMenuItems(allMenus);

return (
  <ul>
    {menuItems.map((item) => (
      <li key={item.name}>
        <button disabled={item.disabled}>{item.name}</button>
      </li>
    ))}
  </ul>
);
*/

// ---------- 6️⃣ Example page-level validation ----------
/*
if (!hasPageAccess("Documents", "add")) {
  alert("You don’t have permission to add a new document.");
  return;
}
*/
